import type {
  Allergy,
  AllergyCreateRequest,
  AllergySearch,
  AllergyStatus,
  AllergyUpdate,
  WithId,
} from '@quromedical/models';
import type { SelectOption } from 'components/types';
import { useCallback, useMemo, useState } from 'react';
import { strings } from 'strings';
import { trpc } from 'trpc';

export interface useAllergyProps {
  items: WithId<Allergy>[];
  isLoading: boolean;
  create: (input: AllergyCreateRequest) => Promise<void>;
  error: string | undefined;
  invalidate: () => Promise<void>;
  setStatus: React.Dispatch<React.SetStateAction<AllergyStatus | undefined>>;
  setPageToken: React.Dispatch<React.SetStateAction<string | undefined>>;
  setCount: React.Dispatch<React.SetStateAction<number | undefined>>;
  nextToken: string | undefined;
  cacheKey: string;
  update: (input: AllergyUpdate) => Promise<void>;
}

type UseAllergyOptions = {
  options: SelectOption[];
  isLoading: boolean;
};

export const useAllergyOptions = (): UseAllergyOptions => {
  const {
    data = [],
    isLoading: isDataLoading,
    isRefetching,
  } = trpc.allergy.getAll.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });

  const options = useMemo(
    () =>
      data.map((allergy) => ({
        display: allergy.name,
        value: `${allergy.code}:${allergy.name}`,
      })),
    [data],
  );

  const isLoading = isDataLoading || isRefetching;

  return { options, isLoading };
};

export const useAllergyActions = (onSuccess?: () => Promise<void>) => {
  const { mutate: createMutation, isPending: isCreateLoading } =
    trpc.patient.createAllergy.useMutation();
  const { mutate: updateMutation, isPending: isUpdateLoading } =
    trpc.patient.updateAllergy.useMutation();

  const update = useCallback(
    async (input: AllergyUpdate) => {
      updateMutation(input, { onSuccess });
    },
    [updateMutation, onSuccess],
  );

  const create = useCallback(
    async (input: AllergyCreateRequest) => {
      createMutation(input, { onSuccess });
    },
    [createMutation, onSuccess],
  );

  const isMutationLoading = isCreateLoading || isUpdateLoading;

  return { isMutationLoading, create, update };
};

export const usePatientAllergy = ({
  patientId,
}: AllergySearch): useAllergyProps => {
  const [status, setStatus] = useState<AllergyStatus | undefined>('active');
  const [pageToken, setPageToken] = useState<string | undefined>('');
  const [count, setCount] = useState<number | undefined>(20);

  const {
    data,
    error: trpcError,
    refetch,
    isFetching,
  } = trpc.patient.getAllergy.useQuery(
    { status, patientId, pageToken, count },
    { refetchOnWindowFocus: false },
  );

  const onSuccess = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const { create, update, isMutationLoading } = useAllergyActions(onSuccess);

  const isLoading = useMemo(() => {
    return isMutationLoading || isFetching;
  }, [isFetching, isMutationLoading]);

  const items = useMemo(() => (data?.items as WithId<Allergy>[]) || [], [data]);
  const nextToken = useMemo(
    () => data?.nextToken as string | undefined,
    [data],
  );

  const error = trpcError ? strings.GenericErrorMessage : undefined;

  const cacheKey = useMemo(
    () =>
      `allergy:${patientId}:${status}:${count}:${pageToken}:${items.length}`,
    [patientId, status, count, pageToken, items.length],
  );

  return {
    items,
    create,
    update,
    isLoading,
    invalidate: onSuccess,
    setStatus,
    setCount,
    setPageToken,
    nextToken,
    cacheKey,
    error,
  };
};
